import {useEffect, useMemo, useState} from 'react'
import Swal from 'sweetalert2'
import type {ManualExamMeasurementCenterDetail} from './core/_models'
import {
  importSecureEExamQuestionSource,
  updateSecureEExamSettings,
} from './core/_requests'

const DEFAULT_MAPPING = JSON.stringify(
  {
    version: 1,
    providerCode: 'question-bank',
    questionsPath: 'data.questions',
    question: {
      idPath: 'id',
      textPath: 'text',
      pointPath: 'point',
      optionsPath: 'options',
      optionIdPath: 'id',
      optionTextPath: 'text',
      optionCorrectPath: 'isCorrect',
    },
  },
  null,
  2
)

type Props = {
  detail: ManualExamMeasurementCenterDetail
  onChanged: () => Promise<void> | void
}

const resolveError = (error: any, fallback: string): string => {
  const payload = error?.response?.data
  if (typeof payload === 'string' && payload.trim()) return payload
  if (typeof payload?.message === 'string' && payload.message.trim()) return payload.message
  return fallback
}

const SecureEExamSettingsPanel = ({detail, onChanged}: Props) => {
  const [enabled, setEnabled] = useState(false)
  const [requirePhoto, setRequirePhoto] = useState(false)
  const [questionSource, setQuestionSource] = useState<'ykys' | 'mapped-json'>('ykys')
  const [externalReference, setExternalReference] = useState('')
  const [mappingJson, setMappingJson] = useState(DEFAULT_MAPPING)
  const [sourcePayload, setSourcePayload] = useState('')
  const [progressInterval, setProgressInterval] = useState(20)
  const [replaceExisting, setReplaceExisting] = useState(false)
  const [saving, setSaving] = useState(false)
  const [importing, setImporting] = useState(false)

  useEffect(() => {
    setEnabled(detail.examDeliveryMethod === 'EExamFaceToFace')
    setRequirePhoto(Boolean(detail.eExamRequireIdentityPhoto))
    setQuestionSource(detail.eExamQuestionSource === 'mapped-json' ? 'mapped-json' : 'ykys')
    setExternalReference(detail.eExamExternalReference ?? '')
    setMappingJson(detail.eExamQuestionMappingJson?.trim() || DEFAULT_MAPPING)
    setProgressInterval(
      Number.isFinite(detail.eExamProgressIntervalSeconds)
        ? Math.min(300, Math.max(10, detail.eExamProgressIntervalSeconds))
        : 20
    )
    setSourcePayload('')
    setReplaceExisting(false)
  }, [detail])

  const isPersistedMappedExam = useMemo(
    () => detail.examDeliveryMethod === 'EExamFaceToFace' && detail.eExamQuestionSource === 'mapped-json',
    [detail.eExamQuestionSource, detail.examDeliveryMethod]
  )
  const controlsDisabled = saving || importing || !detail.eExamCanEdit

  const handleSave = async () => {
    if (enabled && questionSource === 'mapped-json') {
      if (!externalReference.trim()) {
        Swal.fire('Eksik Bilgi', 'Dış sınav referansı zorunludur.', 'warning')
        return
      }
      try {
        JSON.parse(mappingJson)
      } catch {
        Swal.fire('Geçersiz JSON', 'Soru mapping alanı geçerli JSON olmalıdır.', 'warning')
        return
      }
    }

    if (!enabled && detail.examDeliveryMethod === 'EExamFaceToFace') {
      const confirmation = await Swal.fire({
        title: 'E-sınavı kapat',
        text: 'Sınav yüz yüze basılı sınav olarak işaretlenecek.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Devam Et',
        cancelButtonText: 'Vazgeç',
      })
      if (!confirmation.isConfirmed) return
    }

    try {
      setSaving(true)
      await updateSecureEExamSettings(detail.examId, {
        isSecureEExam: enabled,
        requireIdentityPhoto: enabled && requirePhoto,
        questionSource: enabled ? questionSource : 'ykys',
        questionMappingJson: enabled && questionSource === 'mapped-json' ? mappingJson : null,
        externalReference: enabled && questionSource === 'mapped-json' ? externalReference.trim() : null,
        progressIntervalSeconds: Math.min(300, Math.max(10, progressInterval || 20)),
      })
      await onChanged()
      Swal.fire('Kaydedildi', 'Güvenli e-sınav ayarları güncellendi.', 'success')
    } catch (error: any) {
      Swal.fire('Hata', resolveError(error, 'E-sınav ayarları kaydedilemedi.'), 'error')
    } finally {
      setSaving(false)
    }
  }

  const handleImport = async () => {
    let parsedPayload: unknown
    try {
      parsedPayload = JSON.parse(sourcePayload)
      JSON.parse(mappingJson)
    } catch {
      Swal.fire('Geçersiz JSON', 'Mapping ve kaynak veri geçerli JSON olmalıdır.', 'warning')
      return
    }

    if (!externalReference.trim()) {
      Swal.fire('Eksik Bilgi', 'Dış sınav referansı zorunludur.', 'warning')
      return
    }

    if (replaceExisting) {
      const confirmation = await Swal.fire({
        title: 'Soru setini yenile',
        text: 'Mevcut sınav soruları ve kitapçık eşleştirmeleri yeniden oluşturulacak.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'İçe Aktar',
        cancelButtonText: 'Vazgeç',
      })
      if (!confirmation.isConfirmed) return
    }

    try {
      setImporting(true)
      const result = await importSecureEExamQuestionSource(detail.examId, {
        externalReference: externalReference.trim(),
        mappingJson,
        payload: parsedPayload,
        replaceExisting,
      })
      setSourcePayload('')
      setReplaceExisting(false)
      await onChanged()
      Swal.fire(
        'İçe Aktarıldı',
        `${result.importedQuestionCount} soru ve ${result.importedOptionCount} seçenek sınava bağlandı.`,
        'success'
      )
    } catch (error: any) {
      Swal.fire('Hata', resolveError(error, 'Soru kaynağı içe aktarılamadı.'), 'error')
    } finally {
      setImporting(false)
    }
  }

  return (
    <section className='border rounded p-5 bg-light-primary'>
      <div className='d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3 mb-5'>
        <div>
          <h4 className='fw-bold mb-1'>Güvenli Mobil E-Sınav</h4>
          <div className='text-muted small'>A, B, C ve D kitapçıkları Orbit mobil uygulamasında uygulanır.</div>
        </div>
        <label className='form-check form-switch form-check-custom form-check-solid'>
          <input
            className='form-check-input'
            type='checkbox'
            checked={enabled}
            disabled={controlsDisabled}
            onChange={(event) => setEnabled(event.target.checked)}
          />
          <span className='form-check-label fw-semibold'>E-sınav olarak uygula</span>
        </label>
      </div>

      {detail.eExamHasPreparedAttempts && (
        <div className='alert alert-warning py-3'>Öğrenci paketleri hazırlandığı için bu ayarlar kilitlendi.</div>
      )}

      {enabled && (
        <div className='row g-4'>
          <div className='col-lg-6'>
            <label className='form-label fw-semibold'>Soru Kaynağı</label>
            <select
              className='form-select'
              value={questionSource}
              disabled={controlsDisabled}
              onChange={(event) => setQuestionSource(event.target.value as 'ykys' | 'mapped-json')}
            >
              <option value='ykys'>YKYS Ölçme Modülü</option>
              <option value='mapped-json'>JSON ile Bağlanan Soru Bankası</option>
            </select>
          </div>
          <div className='col-lg-6'>
            <label className='form-label fw-semibold' htmlFor='e-exam-progress-interval'>
              İlerleme güncelleme aralığı (saniye)
            </label>
            <input
              id='e-exam-progress-interval'
              type='number'
              min='10'
              max='300'
              className='form-control'
              value={progressInterval}
              disabled={controlsDisabled}
              onChange={(event) => setProgressInterval(Number(event.target.value))}
            />
          </div>
          <div className='col-12'>
            <label className='form-check form-check-custom form-check-solid'>
              <input
                className='form-check-input'
                type='checkbox'
                checked={requirePhoto}
                disabled={controlsDisabled}
                onChange={(event) => setRequirePhoto(event.target.checked)}
              />
              <span className='form-check-label'>Sınav öncesi kimlik fotoğrafı zorunlu</span>
            </label>
          </div>

          {questionSource === 'mapped-json' && (
            <>
              <div className='col-12'>
                <label className='form-label fw-semibold' htmlFor='e-exam-external-reference'>
                  Dış Sınav Referansı
                </label>
                <input
                  id='e-exam-external-reference'
                  className='form-control'
                  maxLength={256}
                  value={externalReference}
                  disabled={controlsDisabled}
                  onChange={(event) => setExternalReference(event.target.value)}
                />
              </div>
              <div className='col-12'>
                <label className='form-label fw-semibold' htmlFor='e-exam-mapping-json'>JSON Mapping</label>
                <textarea
                  id='e-exam-mapping-json'
                  className='form-control font-monospace'
                  rows={13}
                  value={mappingJson}
                  disabled={controlsDisabled}
                  onChange={(event) => setMappingJson(event.target.value)}
                />
              </div>
            </>
          )}
        </div>
      )}

      <div className='d-flex justify-content-end mt-5'>
        <button type='button' className='btn btn-primary' disabled={controlsDisabled} onClick={handleSave}>
          {saving ? 'Kaydediliyor...' : 'E-Sınav Ayarlarını Kaydet'}
        </button>
      </div>

      {enabled && questionSource === 'mapped-json' && isPersistedMappedExam && (
        <div className='border-top mt-5 pt-5'>
          <div className='fw-semibold mb-3'>Soru Kaynağını İçe Aktar</div>
          <textarea
            className='form-control font-monospace'
            rows={10}
            value={sourcePayload}
            disabled={controlsDisabled}
            onChange={(event) => setSourcePayload(event.target.value)}
            placeholder='Soru bankasının JSON çıktısı'
          />
          <div className='d-flex flex-column flex-sm-row align-items-sm-center justify-content-between gap-3 mt-3'>
            <label className='form-check form-check-custom form-check-solid'>
              <input
                className='form-check-input'
                type='checkbox'
                checked={replaceExisting}
                disabled={controlsDisabled}
                onChange={(event) => setReplaceExisting(event.target.checked)}
              />
              <span className='form-check-label'>Mevcut soru setini yenile</span>
            </label>
            <button
              type='button'
              className='btn btn-light-primary'
              disabled={controlsDisabled || !sourcePayload.trim()}
              onClick={handleImport}
            >
              {importing ? 'İçe Aktarılıyor...' : 'JSON Soru Setini İçe Aktar'}
            </button>
          </div>
        </div>
      )}
    </section>
  )
}

export default SecureEExamSettingsPanel
